<template>
  <div
    class="fd-level"
    :class="{ 'fd-level--record': level.type === 'record', 'fd-level--tree': level.type === 'tree' }"
  >
    <!-- Header -->
    <div class="fd-level-header">
      <span
        class="fd-level-drag"
        title="Перетащить"
      >
        <feather-icon
          icon="MenuIcon"
          size="14"
        />
      </span>
      <span class="fd-level-num">{{ index + 1 }}</span>

      <select
        :value="level.type"
        class="fd-level-type"
        @change="update({ type: $event.target.value })"
      >
        <option value="group">
          Группировка
        </option>
        <option value="record">
          Запись
        </option>
        <option value="tree">
          Дерево
        </option>
        <option value="tree_record">
          Дерево + запись
        </option>
      </select>

      <label
        class="fd-level-check"
        title="Разрешить добавление записей"
      >
        <input
          type="checkbox"
          :checked="level.addrecord"
          @change="update({ addrecord: $event.target.checked })"
        >
        <span>+ запись</span>
      </label>

      <div class="fd-level-spacer" />

      <!-- Object name -->
      <span
        v-if="level.objectId"
        class="fd-level-obj-name"
        :title="'Object ID: ' + level.objectId"
      >
        {{ objectName }}
      </span>

      <span
        class="fd-level-toggle"
        @click="update({ expanded: !level.expanded })"
      >
        <feather-icon
          :icon="level.expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          size="14"
        />
      </span>
      <span
        class="fd-level-remove"
        title="Удалить уровень"
        @click="$emit('remove')"
      >
        <feather-icon
          icon="TrashIcon"
          size="14"
        />
      </span>
    </div>

    <!-- Body -->
    <div
      v-if="level.expanded"
      class="fd-level-body"
    >
      <!-- For group levels: single field picker (auto-detects REF vs TEXT from field type) -->
      <div
        v-if="level.type === 'group'"
        class="fd-level-row"
      >
        <label class="fd-label">Поле для группировки</label>
        <select
          :value="level.groupEntity"
          class="fd-group-entity"
          @change="onGroupEntityChange($event.target.value)"
        >
          <option :value="null">
            — выберите поле —
          </option>
          <option
            v-for="f in childFieldsList"
            :key="f.entityId"
            :value="f.entityId"
          >
            {{ f.name }}{{ f.refobjectid ? ' (справочник)' : '' }}
          </option>
        </select>
        <span
          v-if="level.groupEntity && groupModeHint"
          class="fd-group-hint-ok"
        >{{ groupModeHint }}</span>
        <span
          v-if="!childObjectId"
          class="fd-group-hint"
        >⚠ сначала настрой дочерний уровень (источник)</span>
      </div>

      <!-- Source object (for record/tree only — group uses field picker) -->
      <div
        v-if="level.type !== 'group'"
        class="fd-level-row"
      >
        <label class="fd-label">Источник данных</label>
        <div class="fd-obj-picker">
          <input
            v-model="objSearch"
            class="fd-obj-picker-input"
            :placeholder="objectName || '— поиск объекта —'"
            @focus="objDropdownOpen = true"
            @blur="closeObjDropdown"
          >
          <div
            v-if="objDropdownOpen && filteredObjects.length > 0"
            class="fd-obj-dropdown"
          >
            <div
              v-for="obj in filteredObjects"
              :key="obj.id"
              class="fd-obj-dropdown-item"
              :class="{ 'fd-obj-dropdown-item--active': obj.id === level.objectId }"
              @mousedown.prevent="selectObject(obj)"
            >
              <span class="fd-obj-dropdown-name">{{ obj.name }}</span>
              <span class="fd-obj-dropdown-id">#{{ obj.id }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Results mapping (only for record/tree — group auto-generates) -->
      <div
        v-if="level.type === 'tree' || level.type === 'tree_record'"
        class="fd-level-row"
      >
        <label class="fd-label">Связка (results) — откуда → куда</label>
        <div class="fd-results">
          <div
            v-for="(r, ri) in level.results"
            :key="ri"
            class="fd-result-item"
          >
            <select
              class="fd-result-sel"
              :value="r.sourceEntityId"
              @change="updateResult(ri, 'sourceEntityId', Number($event.target.value))"
            >
              <option :value="null">
                — откуда —
              </option>
              <option
                v-for="f in allFieldsList"
                :key="'s'+f.entityId"
                :value="f.entityId"
              >
                {{ f.name }}
              </option>
            </select>
            <span class="fd-result-arrow">→</span>
            <select
              class="fd-result-sel"
              :value="r.destinationEntityId"
              @change="updateResult(ri, 'destinationEntityId', Number($event.target.value))"
            >
              <option :value="null">
                — куда —
              </option>
              <option
                v-for="f in allFieldsList"
                :key="'d'+f.entityId"
                :value="f.entityId"
              >
                {{ f.name }}
              </option>
            </select>
            <span
              class="fd-result-remove"
              @click="removeResult(ri)"
            >✕</span>
          </div>
          <button
            class="fd-add-field-btn fd-add-field-btn--small"
            @click="addResult"
          >
            <feather-icon
              icon="PlusIcon"
              size="11"
            /> Связка
          </button>
        </div>
      </div>

      <!-- Columns -->
      <div class="fd-level-row">
        <div class="fd-columns-header">
          <label class="fd-label">Колонки ({{ level.columns.length }})</label>
          <button
            class="fd-add-field-btn"
            @click="showFieldPicker = true"
          >
            <feather-icon
              icon="PlusIcon"
              size="12"
            />
            Добавить поле
          </button>
        </div>

        <!-- Field picker popup -->
        <div
          v-if="showFieldPicker"
          class="fd-field-picker"
        >
          <input
            ref="fieldSearchInput"
            v-model="fieldSearch"
            class="fd-field-picker-search"
            placeholder="Поиск поля..."
          >
          <div class="fd-field-picker-list">
            <div
              v-for="f in filteredPaletteFields"
              :key="f.entityId"
              class="fd-field-picker-item"
              @click="addFieldFromPicker(f)"
            >
              <span class="fd-field-picker-name">{{ f.name }}</span>
              <span class="fd-field-picker-type">{{ f.datatype }}</span>
            </div>
            <div
              v-if="filteredPaletteFields.length === 0"
              class="fd-field-picker-empty"
            >
              Ничего не найдено
            </div>
          </div>
          <div
            class="fd-field-picker-close"
            @click="showFieldPicker = false"
          >
            Закрыть
          </div>
        </div>

        <draggable
          :value="level.columns"
          handle=".fd-col-drag"
          ghost-class="fd-col-ghost"
          class="fd-columns-list"
          @input="updateColumns($event)"
        >
          <cbs-form-designer-column
            v-for="(col, ci) in level.columns"
            :key="'col-' + ci"
            :column="col"
            :index="ci"
            :is-record-level="level.type === 'record'"
            :entity-map="entityMap"
            :palette-fields="paletteFields"
            @update="onColumnUpdate"
            @remove="$emit('remove-column', ci)"
          />
        </draggable>

        <!-- Drop zone -->
        <div
          class="fd-drop-zone"
          @dragover.prevent
          @drop="onDropField($event)"
        >
          <feather-icon
            icon="DownloadIcon"
            size="14"
          />
          <span>Перетащите поле сюда</span>
        </div>
      </div>

      <!-- Filter -->
      <div class="fd-level-row">
        <label class="fd-label">Фильтр</label>

        <!-- Simple filter editor -->
        <cbs-form-designer-filter
          v-if="!showFilterAdvanced && !showFilterJson"
          :filter="level.filter"
          :entity-map="entityMap"
          :palette-fields="paletteFields"
          :params="formParams"
          :parent-results="parentResults"
          @update="onFilterUpdate"
          @toggle-advanced="showFilterAdvanced = true"
          @toggle-json="showFilterJson = true"
        />

        <!-- Advanced: CbsFilterConstructor -->
        <div
          v-if="showFilterAdvanced && level.filter && level.filter.node"
          class="fd-filter-visual"
        >
          <cbs-filter-constructor
            :node="level.filter.node"
            :fields="filterFields"
            :param-fields="filterParamFields"
            @save="onFilterSave"
          />
          <div class="fd-filter-back">
            <span
              class="fd-link-btn"
              @click="showFilterAdvanced = false"
            >← Простой режим</span>
          </div>
        </div>

        <!-- JSON -->
        <div v-if="showFilterJson">
          <textarea
            :value="filterJsonStr"
            class="fd-filter-json"
            spellcheck="false"
            @input="onFilterJsonEdit($event.target.value)"
          />
          <div class="fd-filter-back">
            <span
              class="fd-link-btn"
              @click="showFilterJson = false"
            >← Назад</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import draggable from 'vuedraggable'
import CbsFilterConstructor from '@/cubus/components/filter/CbsFilterConstructor.vue'
import CbsFormDesignerColumn from './CbsFormDesignerColumn.vue'
import CbsFormDesignerFilter from './CbsFormDesignerFilter.vue'

export default {
  name: 'CbsFormDesignerLevel',
  components: {
    BButton, draggable, CbsFormDesignerColumn, CbsFilterConstructor, CbsFormDesignerFilter,
  },
  props: {
    level: { type: Object, required: true },
    index: { type: Number, required: true },
    totalLevels: { type: Number, default: 0 },
    availableObjects: { type: Array, default: () => [] },
    entityMap: { type: Object, default: () => ({}) },
    paletteFields: { type: Array, default: () => [] },
    formParams: { type: Array, default: () => [] },
    parentResults: { type: Array, default: () => [] },
    childLevel: { type: Object, default: null },
    childFields: { type: Array, default: () => [] },
  },
  data() {
    return {
      showFilterJson: false,
      showFilterAdvanced: false,
      showFieldPicker: false,
      fieldSearch: '',
      objSearch: '',
      objDropdownOpen: false,
    }
  },
  computed: {
    objectName() {
      const obj = this.availableObjects.find(o => o.id === this.level.objectId)
      return obj ? obj.name : `#${this.level.objectId}`
    },
    filterCondCount() {
      if (!this.level.filter || !this.level.filter.node) return 0
      return (this.level.filter.node.conds || []).length
    },
    filterJsonStr() {
      return this.level.filter ? JSON.stringify(this.level.filter, null, 2) : '{}'
    },
    filteredPaletteFields() {
      const q = this.fieldSearch.toLowerCase()
      const fields = this.paletteFields.length > 0 ? this.paletteFields : this.entityMapAsFields
      if (!q) return fields.slice(0, 50)
      return fields.filter(f => f.name.toLowerCase().includes(q) || f.entitySid?.toLowerCase().includes(q)).slice(0, 30)
    },
    entityMapAsFields() {
      return Object.entries(this.entityMap).map(([id, info]) => ({
        entityId: Number(id),
        entitySid: info.sid || '',
        name: info.name || `#${id}`,
        datatype: info.datatype || '',
      }))
    },
    filteredObjects() {
      const q = this.objSearch.toLowerCase().trim()
      const objs = this.availableObjects
      if (!q) return objs.slice(0, 30)
      return objs.filter(o => o.name.toLowerCase().includes(q) || String(o.id).includes(q)).slice(0, 20)
    },
    filterFields() {
      // Fields for CbsFilterConstructor — from palette or entityMap
      return this.allFieldsList.map(f => ({
        value: f.entityId,
        title: f.name,
        entitysid: f.entitySid || '',
        datatype: f.datatype || 'string',
        parententityid: 0,
      }))
    },
    filterParamFields() {
      // paramentity fields — same as filterFields but used as parameter references
      return this.filterFields
    },
    filterConds() {
      return this.level.filter?.node?.conds || []
    },
    allFieldsList() {
      // Merge palette + entityMap for selects
      if (this.paletteFields.length > 0) return this.paletteFields
      return this.entityMapAsFields
    },
    groupMode() {
      return this.level.groupMode || 'ref'
    },
    childObjectId() {
      return this.childLevel ? this.childLevel.objectId : null
    },
    childFieldsList() {
      // Fields from child level; fallback to palette/entityMap
      if (this.childFields && this.childFields.length > 0) return this.childFields
      return this.allFieldsList
    },
    selectedGroupField() {
      if (!this.level.groupEntity) return null
      return this.childFieldsList.find(f => f.entityId === this.level.groupEntity) || null
    },
    groupModeHint() {
      const f = this.selectedGroupField
      if (!f) return ''
      if (f.refobjectid) return `По справочнику (→ obj_${f.refobjectid})`
      return 'По текстовому значению'
    },
  },
  watch: {
    showFieldPicker(val) {
      if (val) {
        this.fieldSearch = ''
        this.$nextTick(() => { this.$refs.fieldSearchInput?.focus() })
      }
    },
  },
  methods: {
    update(changes) { this.$emit('update', changes) },
    onGroupEntityChange(val) {
      const entityId = Number(val) || null
      if (!entityId) {
        this.$emit('update', {
          groupEntity: null, groupMode: 'ref', objectId: null, results: [], groupby: null,
        })
        return
      }
      const field = this.childFieldsList.find(f => f.entityId === entityId)
      const isRef = field && field.refobjectid
      // Auto-generate columns based on child level columns (preserving alignment)
      const newColumns = this.buildAutoColumns(entityId, isRef)
      if (isRef) {
        this.$emit('update', {
          groupEntity: entityId,
          groupMode: 'ref',
          objectId: field.refobjectid,
          results: [{ sourceEntityId: 1, destinationEntityId: entityId }],
          groupby: null,
          columns: newColumns,
        })
      } else {
        this.$emit('update', {
          groupEntity: entityId,
          groupMode: 'text',
          objectId: this.childObjectId,
          results: [{ sourceEntityId: entityId, destinationEntityId: entityId }],
          groupby: {
            isactive: true,
            groups: [{
              isactive: true,
              oper: { sid: 'group', name: 'Group' },
              entity: { value: entityId },
            }],
          },
          columns: newColumns,
        })
      }
    },
    buildAutoColumns(groupEntityId, isRef) {
      // Build columns matching the child level layout so totals align visually.
      // Numeric fields → aggr_sum. Non-numeric → spacer. Group-entity column shows value/name.
      const child = this.childLevel
      if (!child || !Array.isArray(child.columns) || child.columns.length === 0) {
        // Fallback: only the group-entity column
        return [{
          entityId: groupEntityId, type: 'data', editable: false, align: 'left',
        }]
      }
      const numericTypes = new Set(['money', 'decimal', 'double', 'int', 'integer', 'float', 'long', 'number'])
      return child.columns.map((c, idx) => {
        const eid = c.entityId || c.destinationEntityId
        const info = eid ? (this.entityMap[eid] || {}) : {}
        const isNumeric = info.datatype && numericTypes.has(String(info.datatype).toLowerCase())
        // Position of group-entity → show value
        if (eid === groupEntityId) {
          return {
            entityId: groupEntityId, type: 'data', editable: false, align: 'left',
          }
        }
        if (c.type === 'aggr_sum') return { ...c }
        if (isNumeric && eid) {
          return {
            type: 'aggr_sum', align: 'right', destinationEntityId: eid, entityId: null,
          }
        }
        return { type: 'spacer', entityId: null }
      })
    },
    updateColumns(newCols) { this.$emit('update', { columns: newCols }) },
    onColumnUpdate({ index, changes }) {
      const cols = [...this.level.columns]
      cols[index] = { ...cols[index], ...changes }
      this.$emit('update', { columns: cols })
    },
    entName(id) {
      if (!id) return '—'
      const info = this.entityMap[id]
      return info ? info.name : `#${id}`
    },
    removeResult(idx) {
      const results = [...this.level.results]
      results.splice(idx, 1)
      this.$emit('update', { results })
    },
    updateResult(idx, field, value) {
      const results = [...this.level.results]
      results[idx] = { ...results[idx], [field]: value }
      this.$emit('update', { results })
    },
    addResult() {
      this.$emit('update', {
        results: [...this.level.results, { sourceEntityId: 1, destinationEntityId: null }],
      })
    },
    selectObject(obj) {
      this.objSearch = ''
      this.objDropdownOpen = false
      this.update({ objectId: obj.id })
    },
    closeObjDropdown() {
      setTimeout(() => { this.objDropdownOpen = false }, 150)
    },
    addFieldFromPicker(field) {
      this.$emit('add-column', field)
      this.showFieldPicker = false
    },
    onDropField(event) {
      const data = event.dataTransfer.getData('application/json')
      if (data) {
        this.$emit('add-column', JSON.parse(data))
      }
    },
    onFilterUpdate(filter) {
      this.$emit('update', { filter })
    },
    initFilter() {
      this.$emit('update', {
        filter: {
          isActive: true,
          node: {
            isactive: true, isexpanded: true, oper: { id: 1, sid: 'and', name: 'And' }, nodes: [], conds: [],
          },
        },
      })
      this.showFilterVisual = true
    },
    onFilterSave() {
      // CbsFilterConstructor mutates node directly, just trigger reactivity
      this.$emit('update', { filter: { ...this.level.filter } })
    },
    onFilterJsonEdit(value) {
      try {
        const parsed = JSON.parse(value)
        this.$emit('update', { filter: parsed })
      } catch (e) { /* invalid */ }
    },
    condArgLabel(cond, argIdx) {
      if (!cond.args || !cond.args[argIdx]) return ''
      const arg = cond.args[argIdx]
      const typeSid = arg.type?.sid || ''
      if (typeSid === 'entity') {
        return this.entName(arg.value)
      }
      if (typeSid === 'paramentity') {
        return `парам:${this.entName(arg.value)}`
      }
      if (typeSid === 'value') {
        const v = arg.value
        if (v === null || v === undefined) return 'null'
        if (v === false) return 'false'
        if (v === true) return 'true'
        return String(v)
      }
      if (typeSid === 'scalar') {
        return `скаляр:${arg.value}`
      }
      if (typeSid === 'formula') {
        return `формула:${arg.value?.value || arg.value}`
      }
      return String(arg.value || '')
    },
  },
}
</script>

<style scoped>
.fd-level { border: 1px solid #e0e0e0; border-radius: 6px; margin-bottom: 8px; background: #fff; transition: border-color 0.2s; }
.fd-level:hover { border-color: #c0c0c0; }
.fd-level--record { border-left: 3px solid #7367f0; }
.fd-level--tree { border-left: 3px solid #28c76f; }
.fd-level-header { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #fafafa; border-bottom: 1px solid #eee; border-radius: 6px 6px 0 0; flex-wrap: wrap; }
.fd-level-drag { cursor: grab; color: #aaa; }
.fd-level-num { font-size: 11px; font-weight: 700; color: #999; background: #eee; padding: 1px 6px; border-radius: 3px; }
.fd-level-type { font-size: 12px; padding: 3px 8px; border: 1px solid #ddd; border-radius: 4px; background: #fff; }
.fd-level-check { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #888; cursor: pointer; }
.fd-level-spacer { flex: 1; }
.fd-level-obj-name { font-size: 11px; color: #7367f0; background: #f0eeff; padding: 2px 8px; border-radius: 4px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fd-level-toggle, .fd-level-remove { cursor: pointer; color: #999; }
.fd-level-toggle:hover { color: #333; }
.fd-level-remove:hover { color: #e74c3c; }
.fd-level-body { padding: 12px; }
.fd-level-row { margin-bottom: 10px; }
.fd-label { display: block; font-size: 11px; font-weight: 600; color: #888; text-transform: uppercase; margin-bottom: 4px; }
.fd-select { width: 100%; padding: 6px 10px; font-size: 13px; border: 1px solid #ddd; border-radius: 4px; }
.fd-results { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.fd-result-item { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; background: #fff3e0; border-radius: 4px; font-size: 12px; }
.fd-result-remove { cursor: pointer; color: #999; }
.fd-result-remove:hover { color: #e74c3c; }
.fd-link-btn { font-size: 11px; color: #7367f0; cursor: pointer; }
.fd-link-btn:hover { text-decoration: underline; }
/* Object picker */
.fd-obj-picker { position: relative; }
.fd-group-mode { display: flex; gap: 8px; }
.fd-group-mode-opt { display: flex; align-items: center; gap: 5px; padding: 4px 10px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer; font-size: 12px; background: #fff; margin: 0; }
.fd-group-mode-opt:hover { background: #f8f6ff; }
.fd-group-mode-opt--active { background: #e8e5ff; border-color: #7367f0; color: #7367f0; font-weight: 600; }
.fd-group-mode-opt input { margin: 0; }
.fd-group-entity { padding: 4px 10px; font-size: 12px; border: 1px solid #ddd; border-radius: 4px; background: #fff; min-width: 200px; }
.fd-group-hint { font-size: 11px; color: #e65100; margin-left: 8px; }
.fd-group-hint-ok { font-size: 11px; color: #2e7d32; margin-left: 8px; }
.fd-obj-picker-input { width: 100%; padding: 6px 10px; font-size: 13px; border: 1px solid #ddd; border-radius: 4px; outline: none; }
.fd-obj-picker-input:focus { border-color: #7367f0; }
.fd-obj-dropdown { position: absolute; top: 100%; left: 0; right: 0; max-height: 200px; overflow-y: auto; background: #fff; border: 1px solid #ddd; border-top: none; border-radius: 0 0 6px 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 20; }
.fd-obj-dropdown-item { display: flex; justify-content: space-between; padding: 6px 10px; cursor: pointer; font-size: 12px; }
.fd-obj-dropdown-item:hover { background: #e8e5ff; }
.fd-obj-dropdown-item--active { background: #f0eeff; font-weight: 600; }
.fd-obj-dropdown-name { flex: 1; }
.fd-obj-dropdown-id { color: #999; font-size: 10px; }
/* Results */
.fd-result-sel { font-size: 11px; padding: 3px 6px; border: 1px solid #ddd; border-radius: 4px; min-width: 120px; max-width: 180px; }
.fd-result-arrow { color: #999; font-size: 14px; margin: 0 2px; }
.fd-result-remove { cursor: pointer; color: #ccc; font-size: 12px; margin-left: 4px; }
.fd-result-remove:hover { color: #e74c3c; }
.fd-result-item { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; background: #fff8e1; border: 1px solid #ffe082; border-radius: 4px; font-size: 11px; margin-bottom: 4px; }
.fd-add-field-btn--small { padding: 3px 8px; font-size: 11px; }
.fd-columns-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.fd-add-field-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; font-size: 12px; color: #7367f0; background: #f0eeff; border: 1px solid #d4cfff; border-radius: 4px; cursor: pointer; transition: all 0.15s; }
.fd-add-field-btn:hover { background: #7367f0; color: #fff; border-color: #7367f0; }
.fd-columns-list { display: flex; flex-direction: column; gap: 2px; }
.fd-col-ghost { opacity: 0.3; }
.fd-drop-zone { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 6px; border: 2px dashed #eee; border-radius: 4px; color: #ccc; font-size: 11px; margin-top: 4px; transition: all 0.2s; }
.fd-drop-zone:hover { border-color: #7367f0; color: #7367f0; background: #f8f7ff; }
.fd-filter-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; flex-wrap: wrap; }
.fd-filter-actions { display: flex; gap: 8px; margin-left: auto; }
.fd-filter-badge { font-size: 11px; background: #e3f2fd; color: #1565c0; padding: 2px 8px; border-radius: 4px; }
.fd-filter-edit-btn { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; font-size: 11px; color: #1565c0; background: #e3f2fd; border: 1px solid #90caf9; border-radius: 4px; cursor: pointer; transition: all 0.15s; }
.fd-filter-edit-btn:hover { background: #1565c0; color: #fff; border-color: #1565c0; }
.fd-filter-edit-btn--active { background: #1565c0; color: #fff; border-color: #1565c0; }
.fd-filter-visual { border: 1px solid #e0e0e0; border-radius: 6px; padding: 8px; margin-top: 4px; background: #fafafa; }
.fd-filter-back { margin-top: 6px; }
.fd-filter-json { width: 100%; min-height: 80px; max-height: 200px; font-family: monospace; font-size: 11px; padding: 6px; border: 1px solid #ddd; border-radius: 4px; resize: vertical; margin-top: 4px; background: #fafafa; }
.fd-filter-summary { display: flex; flex-direction: column; gap: 2px; margin-top: 4px; }
.fd-filter-cond { display: flex; align-items: center; gap: 4px; font-size: 11px; padding: 3px 8px; background: #f5f5f5; border-radius: 3px; }
.fd-filter-cond-ent { color: #1565c0; font-weight: 500; }
.fd-filter-cond-op { color: #888; }
.fd-filter-cond-val { color: #2e7d32; }
.fd-field-picker { position: relative; border: 1px solid #ddd; border-radius: 6px; background: #fff; padding: 8px; margin-bottom: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10; }
.fd-field-picker-search { width: 100%; padding: 6px 10px; font-size: 13px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 6px; }
.fd-field-picker-list { max-height: 200px; overflow-y: auto; }
.fd-field-picker-item { display: flex; justify-content: space-between; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 12px; }
.fd-field-picker-item:hover { background: #e8e5ff; }
.fd-field-picker-name { font-weight: 500; }
.fd-field-picker-type { font-size: 10px; color: #999; }
.fd-field-picker-empty { text-align: center; padding: 12px; color: #999; font-size: 12px; }
.fd-field-picker-close { text-align: center; padding: 4px; font-size: 11px; color: #999; cursor: pointer; border-top: 1px solid #eee; margin-top: 4px; }
.fd-field-picker-close:hover { color: #7367f0; }
</style>
